/**
 * Modal feasibility engine (spec §6). These calls go DIRECT to the Modal host
 * from the browser — not through the Xano `api` seam — so they live here with a
 * tiny dedicated fetch. CORS: the Modal endpoints must allow the app's origin
 * (the old Retool flow called Modal server-side, so this is the first time the
 * browser exercises it). A failure surfaces as a thrown Error.
 *
 * Three-stage flow: analyze (rule-based, free, debounced) → generate (returns a
 * call_id) → poll result (~2s) until done. On done the result's `pdf_url` is a
 * `data:application/pdf;base64,…` URI carrying the bytes inline.
 */

import { config } from "@/config/env";
import { getStaffToken } from "@/lib/session";
import type { ConstraintsReport, ProjectBrief, ProjectBriefExtension, ProjectBriefProperty } from "@/types";

const base = config.modalFeasibilityBase;

/** A clarifying follow-up returned by /analyze. `extension_index` says where the
 *  answer belongs: `null`/absent = a property-level field (or top-level "budget_band");
 *  an integer targets that line in `extensions[]`. */
export interface Question {
  key: string;
  prompt: string;
  type: "choice" | "number" | "boolean" | "text";
  source?: "fixed" | "constraint" | "dynamic";
  options?: string[];
  unit?: string | null;
  rationale?: string;
  required?: boolean;
  extension_index?: number | null;
}

/** POST /analyze body — top-level site/budget fields + the property block + the
 *  extensions[] array (mirrors the engine's AnalyzeInput; everything partial). */
export interface AnalyzeBody {
  description?: string;
  address?: string;
  council?: string;
  uprn?: string;
  latitude?: number;
  longitude?: number;
  budget_band?: string;
  property?: Partial<ProjectBriefProperty>;
  extensions?: Partial<ProjectBriefExtension>[];
}

/** Result of POST /analyze — drives the smart follow-ups + readiness. */
export interface AnalyzeResult {
  ready: boolean;
  questions: Question[];
  constraints: Record<string, unknown> | null;
  lookup_key?: string | null;
}

/** The `result` payload of a finished report (Modal /result done). */
export interface FeasibilityResult {
  /** data:application/pdf;base64,… — the bytes inline (Modal hosts nothing). */
  pdf_url: string;
  band: string;
  score_pct: number;
  confidence: string;
  route: string;
  constraint_names: string[];
  case_study_count: number;
  approval_rate_pct: number;
  determination_days: number;
  /** Structured site constraints (property-level). Stored on the lead via the `summary`
   *  passthrough — the modal spreads the whole result (minus pdf_url) into `summary`. */
  constraints: ConstraintsReport;
}

export interface PollResponse {
  status: "running" | "done" | "expired" | "error";
  result?: FeasibilityResult;
  error?: string;
}

/** POST /generate body — the analyze body plus the two generate-time additions. */
export interface GenerateBody extends AnalyzeBody {
  client_name?: string;
  /** The published quote's public URL — drives the report's CTA. */
  quote_url?: string;
  /** Web view (from `beginFeasibilityReport`). With a token the engine also renders the web page,
   *  uploads both artefacts to R2 under version-scoped keys, and fires the Xano callback that
   *  flips the report to `ready`. Without one it renders the PDF and tells nobody. */
  report_token?: string;
  report_version?: number;
  /** quote | general — stamped on the report, and on every session that reads it. */
  report_cta_variant?: "quote" | "general";
}

async function modalFetch<T>(path: string, init: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", Accept: "application/json", ...init.headers },
    });
  } catch {
    throw new Error(
      "Could not reach the feasibility engine. If this persists it may be a CORS or network issue.",
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // FastAPI's HTTPException body is {"detail": "…"}; show the message, not the JSON.
    let message = text;
    try {
      const parsed = JSON.parse(text) as { detail?: unknown };
      if (typeof parsed?.detail === "string") message = parsed.detail;
    } catch {
      /* not JSON — use the raw text */
    }
    throw new Error(message || `Feasibility request failed (${res.status}).`);
  }
  return (await res.json()) as T;
}

/** GET /health — pre-warm the engine. The web function scales to zero between uses and a cold
 *  start is ~5 s, so the lead drawer fires this (fire-and-forget) as it opens; by the time staff
 *  reach Suggest the container is up and the click costs only the model call. Never throws —
 *  a failed warm-up just means the click is slower. */
export function warmEngine(): void {
  try {
    void fetch(`${base}/health`, { method: "GET", keepalive: true }).catch(() => undefined);
  } catch {
    /* ignore — warming is best-effort */
  }
}

/** POST /quote-description — draft the quote's short description (the noun phrase completing
 *  "…obtain permission for your ___") from the lead's enquiry text and brief. A suggestion for
 *  staff to edit, never applied silently.
 *
 *  The one engine route that is staff-gated: it carries the Xano staff JWT and the engine
 *  verifies it against Xano `auth/me` before spending a model call. Without a token we don't
 *  bother the engine — the answer would be a 401. */
export async function suggestQuoteDescription(body: {
  project_description: string;
  project_brief?: ProjectBrief | null;
}): Promise<string> {
  const token = getStaffToken();
  if (!token) throw new Error("Sign in again to use suggestions.");
  const res = await modalFetch<{ description: string }>("/quote-description", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return res.description;
}

/** POST /analyze — rule-based (no LLM, no cost). Fire debounced on form change. */
export function analyzeFeasibility(body: AnalyzeBody, signal?: AbortSignal): Promise<AnalyzeResult> {
  return modalFetch<AnalyzeResult>("/analyze", { method: "POST", body: JSON.stringify(body), signal });
}

/** POST /generate — kicks off the (LLM-backed) report; returns a call_id. */
export function generateFeasibility(body: GenerateBody): Promise<{ call_id: string }> {
  return modalFetch<{ call_id: string }>("/generate", { method: "POST", body: JSON.stringify(body) });
}

/** GET /result/{call_id} — poll ~2s until status leaves "running".
 *
 *  The one route that tolerates a non-2xx instead of going through `modalFetch`:
 *  the engine answers 404 for an expired result and 500 for a failed pipeline, and
 *  in both cases the body is a `PollResponse` naming the reason. Throwing on those
 *  would surface the raw JSON as the error text and leave the caller's "expired"
 *  and "error" branches unreachable. */
export async function pollFeasibilityResult(callId: string): Promise<PollResponse> {
  let res: Response;
  try {
    res = await fetch(`${base}/result/${encodeURIComponent(callId)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
  } catch {
    throw new Error(
      "Could not reach the feasibility engine. If this persists it may be a CORS or network issue.",
    );
  }
  const body = (await res.json().catch(() => null)) as PollResponse | null;
  if (body?.status) return body;
  throw new Error(`Feasibility request failed (${res.status}).`);
}

/** POST /constraints body — a standalone point lookup (no full brief). Prefers the
 *  UPRN; falls back to lat/lng. */
export interface ConstraintsLookupBody {
  uprn?: string;
  latitude?: number;
  longitude?: number;
}

/** POST /constraints — standalone planning-constraints lookup for one site
 *  (planning.data.gov.uk, deterministic; no LLM). Prefers UPRN, falls back to
 *  lat/lng. Returns a ConstraintsReport; `lookup_ok:false` means the lookup didn't
 *  complete (flags are *unknown*, not "all clear"). Used to tag a job on demand —
 *  the Job Detail "Check planning constraints" action, then persisted via
 *  jobsApi.setJobConstraints. Throws (never 500s) only on a network/CORS failure. */
export function lookupConstraints(body: ConstraintsLookupBody): Promise<ConstraintsReport> {
  return modalFetch<ConstraintsReport>("/constraints", { method: "POST", body: JSON.stringify(body) });
}
